import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AlertService, AuthService, SubscribersService} from '@coreservices';
import {Constants} from 'src/app/admin-core/constants';
import {NotificationService} from 'src/app/admin-core/apiservices/notification.service';
import {BackendUserService} from 'src/app/admin-core/apiservices/backend-user.service';
import {RequestParamModel} from 'src/app/admin-core/models/request-params.model';
import {applyReaction, NoteStateService} from 'src/app/admin-core/services/note-state/note-state.service';
import {endLoading, repaintView} from 'src/app/admin-core/utils/loading-state.util';
import {
  likeTargetNote,
  notificationBody,
  notificationMessageHtml,
  NotificationChip,
  notificationChip,
  notificationSenderName,
  notificationTarget,
  NotificationTarget,
  notificationTitle,
  notificationType,
  NotificationRowType,
  notificationTypeLabel,
  notificationUserImage,
  rememberedSender,
  rememberSender,
} from 'src/app/admin-core/utils/notification-row.util';
import {DateRangeChange, DateRangeFilterComponent} from 'src/app/shared/components/date-range-filter/date-range-filter.component';
import {PebblConfirmComponent} from 'src/app/shared/components/pebbl-confirm/pebbl-confirm.component';

type ReadView = 'unread' | 'all' | 'read';
type Source = 'all' | 'team' | 'system';

/** Rows per page — the console's notification page size. */
const PAGE_SIZE = 20;

/** The largest window a refresh re-reads in one request (api-v2's cap). */
const MAX_REFRESH_SIZE = 500;

/** The search box waits this long after the last keystroke before it queries. */
const SEARCH_DEBOUNCE_MS = 300;

/** Day buckets and clock times follow the client's clock (Sydney), not the browser's. */
const SYDNEY = 'Australia/Sydney';

/** What this page passes to `updateUnreadNotification` after its own writes, so
 *  the header re-reads the bell count. */
const PAGE_SIGNAL = 'notificationsPage';

/** A run of row text, cut at the search hits. */
interface TextSeg {
  text: string;
  hit: boolean;
}

/** A notification as the page renders it: the API row plus what is worked out
 *  once when it arrives. */
interface PageRow {
  raw: any;
  id: number;
  type: NotificationRowType;
  title: TextSeg[];
  /** The title as plain text, for the Open action's label. */
  titleText: string;
  html: string;
  /** "Order · O-WEB-4359", "Mention · OH Architecture" — null with no record to name. */
  chip: NotificationChip | null;
  target: NotificationTarget | null;
  /** The sender's photo; null shows `initials` instead (the design's fallback). */
  photo: string | null;
  sender: string;
  /** The sender's initials, or the type's ("S" for a system row with no sender). */
  initials: string;
  timestamp: number;
  dayKey: string;
}

interface DayGroup {
  key: string;
  label: string;
  items: PageRow[];
}

interface Person {
  id: number;
  name: string;
  picture: string | null;
  initials: string;
}

interface FeedCounts {
  total: number;
  unread_total: number;
  view: {unread: number; all: number; read: number};
  source: {all: number; team: number; system: number};
  type: Record<NotificationRowType, number>;
  people: {from_user_id: number; count: number}[];
}

/**
 * View all notifications (/notifications) — the panel's "View all" target, built
 * from console-v2's design (`notifications-page`):
 *
 * - Unread / All / Read (Read is the "completed" list, newest received first)
 *   and All sources / Team / System, a search box, and a Filters panel (Type,
 *   From, Received) with removable chips.
 * - Every count is the server's, under every OTHER filter, so a count is the
 *   number of rows picking it would show (api-v2 `notification/feed-counts`).
 * - The list (api-v2 `notification/feed`) is grouped by Sydney calendar day and
 *   pages 20 at a time as its end scrolls into view, with "Show more" as the
 *   fallback. The cursor is the last row's id, so rows leaving a view as they are
 *   read never make a page skip the rows behind them.
 *
 * A row reads as in the notification panel (type, title, chip, destination —
 * notification-row.util). Opening it does not mark it read — only the read icon
 * (or a 👍) does. Its avatar is the design's:
 * the sender's photo, else initials. The Unread /
 * All choice is remembered with the panel's own "Only show unread" setting.
 */
@Component({
  standalone: false,
  selector: 'app-notifications',
  // Eager: this Angular version defaults every component to OnPush (see app.component.ts).
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './notifications.component.html',
  styleUrls: ['../../../shared/styles/listing-page.css', './notifications.component.css']
})
export class NotificationsComponent implements OnInit, OnDestroy {
  readonly types: {id: NotificationRowType; label: string}[] = [
    {id: 'mention', label: 'Mentions and replies'},
    {id: 'order', label: 'Orders'},
    {id: 'quote', label: 'Quotes'},
    {id: 'sample', label: 'Sample requests'},
    {id: 'system', label: 'System alerts'}
  ];

  /* ── view and filters ──────────────────────────────────────── */
  /** Unread / All follows the panel's "Only show unread" (on when never set). */
  view: ReadView = localStorage.getItem('notif_only_unread') !== 'false' ? 'unread' : 'all';
  source: Source = 'all';
  query = '';
  type: '' | NotificationRowType = '';
  /** From; null is Everyone. Shared with the panel's sender menu and remembered
   *  like Unread / All (see `rememberedSender`). */
  personId: number | null = null;
  /** The remembered pick, which names the From filter until `staff` arrives. */
  private rememberedPerson: Person | null = null;
  range: {from: string; to: string; label: string} | null = null;
  filtersOpen = false;
  typeOpen = false;
  personOpen = false;
  personQuery = '';

  /* ── data ──────────────────────────────────────────────────── */
  rows: PageRow[] = [];
  groups: DayGroup[] = [];
  /** Rows matching the filters (server total) and how many are still unloaded. */
  total = 0;
  remaining = 0;
  counts: FeedCounts | null = null;
  /** The full staff list (the @-mention picker's), for the From filter. */
  staff: Person[] = [];
  private peopleCounts: Record<number, number> = {};
  private failedPhotos = new Set<number>();
  private failedRowPhotos = new Set<number>();

  /** First paint — the page loader. */
  loading = true;
  /** A filter / search change is being fetched; the list stays as it is until the new rows arrive. */
  refreshing = false;
  loadingMore = false;
  /** Mark all as read is being saved; the button stays off until it lands. */
  markingAll = false;

  private userId: any;
  private userProfile: any;
  private readonly readBusy = new Set<any>();
  /** Ticket for the newest list request; older answers are dropped. */
  private seq = 0;
  private searchTimer: any = null;
  private observer?: IntersectionObserver;
  private sentinelEl: HTMLElement | null = null;
  private destroyed = false;
  private readonly destroy$ = new Subject<void>();

  @ViewChild('received') private received?: DateRangeFilterComponent;
  @ViewChild('personSearch') private personSearch?: ElementRef<HTMLInputElement>;

  /** The 1px marker under the list; re-armed whenever it mounts or moves. */
  @ViewChild('sentinel') set sentinelRef(el: ElementRef<HTMLElement> | undefined) {
    this.sentinelEl = el?.nativeElement ?? null;
    this.armObserver();
  }

  constructor(
    private alertService: AlertService,
    private router: Router,
    private changeDetection: ChangeDetectorRef,
    private zone: NgZone,
    private subscribersService: SubscribersService,
    private notificationService: NotificationService,
    private backendUserService: BackendUserService,
    private authService: AuthService,
    private title: Title,
    private noteState: NoteStateService,
    private ngbModal: NgbModal,
  ) {
    this.userProfile = JSON.parse(JSON.stringify(this.authService.getAdminUser() ?? {}));
    this.userId = this.userProfile?.id;
    // Open on the person picked last time, here or in the panel.
    this.rememberedPerson = rememberedSender(this.userId);
    this.personId = this.rememberedPerson?.id ?? null;
    this.subscribersService.emitNotificationSidebarStatus(false);
  }

  /************************************
   * Life cycle hooks start
   ************************************/

  ngOnInit(): void {
    this.title.setTitle('Notifications | GatherCo');
    // The redesigned pages' ground: `--bg`, the dark-theme scope and shared responsive rules.
    document.body.classList.add('dashboard-active');
    this.loadStaff();
    this.fetchTop(PAGE_SIZE);

    // A like or read/unread made on a notes card (or the panel) lands on the
    // matching rows here, so the two never disagree until a reload.
    this.noteState.changes$.pipe(takeUntil(this.destroy$)).subscribe((change) => {
      let changed = false;
      this.rows.forEach((row) => {
        if (change.kind === 'read') {
          if (row.raw.id == change.notificationId && row.raw.status != change.status) {
            row.raw.status = change.status;
            changed = true;
          }
          return;
        }
        const note = likeTargetNote(row.raw);
        if (note?.id == change.noteId) {
          changed = applyReaction(note, change) || changed;
        }
      });
      if (changed) {
        repaintView(this.changeDetection);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.observer?.disconnect();
    clearTimeout(this.searchTimer);
    document.body.classList.remove('dashboard-active');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /************************************
   * Life cycle hooks end
   ************************************/

  /************************************
   * Fetching start
   ************************************/

  /** The filters as api-v2's query string names them. */
  private filterParams(): Record<string, any> {
    return {
      view: this.view,
      source: this.source,
      type: this.type,
      from_user_id: this.personId,
      start_date: this.range?.from,
      end_date: this.range?.to,
      q: this.query.trim(),
    };
  }

  /** A filter changed: page one of the new set, fresh counts. */
  private reload(): void {
    this.refreshing = true;
    this.fetchTop(PAGE_SIZE);
  }

  /** Re-read the rows already loaded after a read / unread / like, so a row that
   *  left the view is replaced by the next one. */
  private refresh(): void {
    this.fetchTop(Math.min(Math.max(this.rows.length, PAGE_SIZE), MAX_REFRESH_SIZE));
  }

  /** The top `size` rows of the current view, replacing the list, plus its counts. */
  private fetchTop(size: number): void {
    const seq = ++this.seq;
    this.loadCounts(seq);
    this.notificationService.getNotificationFeed({...this.filterParams(), limit: size}).pipe(
      takeUntil(this.destroy$),
      endLoading(this.changeDetection, () => {
        if (seq === this.seq) {
          this.loading = false;
          this.refreshing = false;
          this.armObserver();
        }
      })
    ).subscribe({
      next: (page) => {
        if (seq !== this.seq) {
          return;
        }
        this.setRows((page.rows || []).map((row: any) => this.toPageRow(row)));
        this.total = page.total || 0;
        this.remaining = page.remaining || 0;
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  /** The next page after the last row shown, appended. */
  loadMore(): void {
    const last = this.rows[this.rows.length - 1];
    if (!last || this.loadingMore || this.refreshing || this.remaining <= 0) {
      return;
    }
    const seq = this.seq;
    this.loadingMore = true;
    this.notificationService.getNotificationFeed({...this.filterParams(), limit: PAGE_SIZE, before_id: last.id}).pipe(
      takeUntil(this.destroy$),
      endLoading(this.changeDetection, () => {
        this.loadingMore = false;
        // A page that does not push the marker out of view keeps loading: the
        // observer only reports a CHANGE, so it is re-armed after every page.
        this.armObserver();
      })
    ).subscribe({
      next: (page) => {
        // The view changed while this page was in flight.
        if (seq !== this.seq) {
          return;
        }
        const held = new Set(this.rows.map((row) => row.id));
        const fresh = (page.rows || []).filter((row: any) => !held.has(row.id)).map((row: any) => this.toPageRow(row));
        this.setRows([...this.rows, ...fresh]);
        this.remaining = page.remaining || 0;
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  /** Header, segment, Type and From counts for the current filters. */
  private loadCounts(seq: number): void {
    this.notificationService.getNotificationFeedCounts(this.filterParams()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (counts: FeedCounts | null) => {
        if (seq !== this.seq || !counts) {
          return;
        }
        this.counts = counts;
        this.peopleCounts = Object.fromEntries((counts.people || []).map((row) => [Number(row.from_user_id), Number(row.count)]));
        this.subscribersService.updateNotificationBellIcon(counts.unread_total > 0);
        repaintView(this.changeDetection);
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  /** The @-mention picker's staff list, as the From filter's options. */
  private loadStaff(): void {
    const params = new RequestParamModel();
    params.fields = ['id', 'first_name', 'last_name', 'profile_picture'];
    params.pageSize = 'all';
    this.backendUserService.getBackEndUser(params, true).pipe(takeUntil(this.destroy$)).subscribe({
      next: (list: any) => {
        this.staff = (list?.data || [])
          .map((user: any) => {
            const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
            return {id: Number(user.id), name, picture: (user.profile_picture || '').trim() || null, initials: initials(name)};
          })
          .filter((person: Person) => person.name)
          .sort((a: Person, b: Person) => a.name.localeCompare(b.name));
        repaintView(this.changeDetection);
      },
      error: () => { /* non-blocking — From just offers Everyone */ },
    });
  }

  private setRows(rows: PageRow[]): void {
    this.rows = rows;
    const groups: DayGroup[] = [];
    for (const row of rows) {
      const last = groups[groups.length - 1];
      if (last?.key === row.dayKey) {
        last.items.push(row);
      } else {
        groups.push({key: row.dayKey, label: dayLabel(row.timestamp), items: [row]});
      }
    }
    this.groups = groups;
  }

  /** Everything about a row that only changes when it is fetched again. */
  private toPageRow(raw: any): PageRow {
    const timestamp = sydneyInstant(raw.created_at);
    return {
      raw,
      id: raw.id,
      type: notificationType(raw),
      title: this.textSegs(notificationTitle(raw)),
      titleText: notificationTitle(raw),
      html: notificationMessageHtml(notificationBody(raw), this.query.trim()),
      chip: notificationChip(raw),
      target: notificationTarget(raw.note_reaction_id ? raw.noteReaction : raw),
      photo: (raw.fromUser?.profile_picture || '').trim() ? notificationUserImage(raw.fromUser.profile_picture) : null,
      sender: notificationSenderName(raw.fromUser),
      initials: initials(raw.fromUser ? notificationSenderName(raw.fromUser) : notificationTypeLabel(notificationType(raw))),
      timestamp,
      dayKey: dayKey(timestamp),
    };
  }

  /** IntersectionObserver on the marker under the list, 400px early, so the next
   *  page is in before the reader reaches the end. */
  private armObserver(): void {
    this.observer?.disconnect();
    if (this.destroyed || !this.sentinelEl || typeof IntersectionObserver === 'undefined') {
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }
      // IntersectionObserver callbacks run outside Angular's zone.
      this.zone.run(() => {
        this.loadMore();
        repaintView(this.changeDetection);
      });
    }, {rootMargin: '0px 0px 400px 0px'});
    this.observer.observe(this.sentinelEl);
  }

  /************************************
   * Fetching end
   ************************************/

  /************************************
   * View controls start
   ************************************/

  setView(view: ReadView): void {
    if (this.view === view) {
      return;
    }
    this.view = view;
    // Unread / All is the panel's "Only show unread"; Read is not remembered.
    if (view !== 'read') {
      localStorage.setItem('notif_only_unread', String(view === 'unread'));
    }
    this.reload();
  }

  setSource(source: Source): void {
    if (this.source === source) {
      return;
    }
    this.source = source;
    this.reload();
  }

  onQuery(value: string): void {
    this.query = value;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.reload(), SEARCH_DEBOUNCE_MS);
  }

  setType(type: '' | NotificationRowType): void {
    this.typeOpen = false;
    if (this.type === type) {
      return;
    }
    this.type = type;
    this.reload();
  }

  setPerson(id: number | null): void {
    this.personOpen = false;
    if (this.personId === id) {
      return;
    }
    this.personId = id;
    this.rememberPerson();
    this.reload();
  }

  /** Keeps the From pick for the next time the page or the panel opens. */
  private rememberPerson(): void {
    this.rememberedPerson = this.person;
    rememberSender(this.userId, this.rememberedPerson);
  }

  onRange(change: DateRangeChange): void {
    const range = change.key && change.from && change.to
      ? {from: ymd(change.from), to: ymd(change.to), label: `${dayMonthYear(change.from)} - ${dayMonthYear(change.to)}`}
      : null;
    if (range?.from === this.range?.from && range?.to === this.range?.to) {
      return;
    }
    this.range = range;
    this.reload();
  }

  toggleMenu(which: 'type' | 'person', event: Event): void {
    event.stopPropagation();
    this.personQuery = '';
    const wasOpen = which === 'type' ? this.typeOpen : this.personOpen;
    // One popover at a time, app-wide (QA, 3 Nov): closes the Received picker,
    // whose own outside-click close never sees this click — the field stops it.
    // Read `wasOpen` first: the event runs `closeMenus()` here too.
    document.dispatchEvent(new Event('gc-close-popovers'));
    this.typeOpen = which === 'type' && !wasOpen;
    this.personOpen = which === 'person' && !wasOpen;
    if (this.personOpen) {
      // Search-first picker: the box takes the focus as it opens.
      setTimeout(() => this.personSearch?.nativeElement.focus());
    }
  }

  /** Type, From and Received back to their defaults, in one reload. */
  clearFilters(): void {
    this.type = '';
    this.personId = null;
    this.rememberPerson();
    this.typeOpen = false;
    this.personOpen = false;
    this.clearRangeThenReload();
  }

  /** Everything back to the start — the empty state's "Clear filters". */
  clearAll(): void {
    this.view = 'all';
    localStorage.setItem('notif_only_unread', 'false');
    this.source = 'all';
    this.query = '';
    clearTimeout(this.searchTimer);
    this.clearFilters();
  }

  /** Resetting the date picker emits a cleared range, which reloads; with no
   *  range set there is nothing to emit, so reload directly. */
  private clearRangeThenReload(): void {
    if (this.range && this.received) {
      this.received.reset();
      return;
    }
    this.range = null;
    this.reload();
  }

  removeRange(): void {
    this.clearRangeThenReload();
  }

  /** `gc-close-popovers` is the Received picker opening — it stops its own click. */
  @HostListener('document:gc-close-popovers')
  @HostListener('document:click')
  closeMenus(): void {
    if (this.typeOpen || this.personOpen) {
      this.typeOpen = false;
      this.personOpen = false;
      repaintView(this.changeDetection);
    }
  }

  /************************************
   * View controls end
   ************************************/

  /************************************
   * Counts and labels start
   ************************************/

  get unreadTotal(): number {
    return this.counts?.unread_total ?? 0;
  }

  get inboxTotal(): number {
    return this.counts?.total ?? 0;
  }

  /** Type, From and Received — the Filters button's badge. */
  get filterCount(): number {
    return (this.type ? 1 : 0) + (this.personId ? 1 : 0) + (this.range ? 1 : 0);
  }

  /** Anything narrowing the list — the empty state's wording and its Clear filters. */
  get narrowed(): boolean {
    return this.filterCount > 0 || this.source !== 'all' || this.view !== 'all' || !!this.query.trim();
  }

  private get narrowedBeyondView(): boolean {
    return this.filterCount > 0 || this.source !== 'all' || !!this.query.trim();
  }

  get emptyTitle(): string {
    return this.view === 'unread' && !this.narrowedBeyondView ? "You're all caught up" : 'No notifications';
  }

  get emptyText(): string {
    if (!this.inboxTotal) {
      return 'New activity will show up here.';
    }
    if (this.view === 'unread' && !this.narrowedBeyondView) {
      return 'No unread notifications right now.';
    }
    return 'Nothing matches - clear a filter or the search.';
  }

  typeCount(type: NotificationRowType): number {
    return this.counts?.type?.[type] ?? 0;
  }

  typeFilterLabel(type: '' | NotificationRowType): string {
    return this.types.find((option) => option.id === type)?.label ?? 'All types';
  }

  /** Staff narrowed by the picker's search (any part of the name). */
  get peopleHits(): Person[] {
    const query = this.personQuery.trim().toLowerCase();
    return query ? this.staff.filter((person) => person.name.toLowerCase().includes(query)) : this.staff;
  }

  /** Rows from this person under the other filters; 0 is muted but still pickable. */
  personCount(id: number): number {
    return this.peopleCounts[id] ?? 0;
  }

  get person(): Person | null {
    return this.staff.find((person) => person.id === this.personId)
      ?? (this.personId != null && this.rememberedPerson?.id === this.personId ? this.rememberedPerson : null);
  }

  personPhoto(person: Person): string | null {
    return person.picture && !this.failedPhotos.has(person.id) ? notificationUserImage(person.picture) : null;
  }

  onPersonPhotoError(id: number): void {
    this.failedPhotos.add(id);
  }

  /** A row's photo, unless it has failed to load — then its initials show. */
  rowPhoto(row: PageRow): string | null {
    return row.photo && !this.failedRowPhotos.has(row.id) ? row.photo : null;
  }

  onRowPhotoError(row: PageRow): void {
    this.failedRowPhotos.add(row.id);
  }

  typeLabel(type: string): string {
    return notificationTypeLabel(type);
  }

  /** Title text cut at the search hits — every occurrence, case-insensitive. */
  private textSegs(text: string): TextSeg[] {
    const query = this.query.trim().toLowerCase();
    if (!query) {
      return [{text, hit: false}];
    }
    const segs: TextSeg[] = [];
    const low = text.toLowerCase();
    let at = 0;
    for (let i = low.indexOf(query); i >= 0; i = low.indexOf(query, at)) {
      if (i > at) {
        segs.push({text: text.slice(at, i), hit: false});
      }
      segs.push({text: text.slice(i, i + query.length), hit: true});
      at = i + query.length;
    }
    if (at < text.length) {
      segs.push({text: text.slice(at), hit: false});
    }
    return segs;
  }

  /** Compact relative time, the design's format: "just now", "5m ago", "2h ago", "3d ago", "2w ago". */
  timeAgo(timestamp: number): string {
    const minutes = Math.floor((Date.now() - timestamp) / 60_000);
    if (minutes < 1) {
      return 'just now';
    }
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return days < 7 ? `${days}d ago` : `${Math.floor(days / 7)}w ago`;
  }

  clock(timestamp: number): string {
    return new Date(timestamp).toLocaleTimeString('en-AU', {hour: 'numeric', minute: '2-digit', timeZone: SYDNEY}).toLowerCase();
  }

  formatCount(value: number): string {
    return value.toLocaleString('en-AU');
  }

  /************************************
   * Counts and labels end
   ************************************/

  /************************************
   * Row actions start
   ************************************/

  isUnread(row: PageRow): boolean {
    return row.raw.status != Constants.NOTIFICATION_STATUS_ACTIVE;
  }

  /** A click anywhere on the row opens the record — except on its controls. */
  onRowClick(event: MouseEvent, row: PageRow): void {
    if ((event.target as HTMLElement).closest('a, button')) {
      return;
    }
    this.open(row);
  }

  /** Goes to the row's record. Opening leaves the read state alone: a
   *  notification stays unread until its read icon (or 👍) is clicked. */
  open(row: PageRow): void {
    if (row.target) {
      this.router.navigate(row.target.commands, {queryParams: row.target.queryParams});
    }
  }

  /** Mark as read ↔ unread. */
  toggleRead(row: PageRow): void {
    this.saveRead(row.raw, this.isUnread(row));
  }

  readSaving(row: PageRow): boolean {
    return this.readBusy.has(row.id);
  }

  /**
   * `PATCH notification/{id}` — then every notes card showing the note follows,
   * the bell re-reads its count, and the page refreshes so a row that left the
   * view (read under Unread, unread under Read) is replaced by the next one.
   */
  private saveRead(notification: any, read: boolean, quiet: boolean = false): void {
    if (!notification?.id || this.readBusy.has(notification.id)) {
      return;
    }
    this.readBusy.add(notification.id);
    this.notificationService.markNotificationAsRead(this.readBody(notification, read), notification.id).pipe(
      takeUntil(this.destroy$),
      endLoading(this.changeDetection, () => this.readBusy.delete(notification.id))
    ).subscribe({
      next: (response: any) => {
        if (!quiet) {
          this.alertService.success(read
            ? Constants.NOTIFICATION_MARKED_AS_READ_SUCCESS_MESSAGE
            : Constants.NOTIFICATION_MARKED_AS_UNREAD_SUCCESS_MESSAGE);
        }
        this.afterRead(notification, Number(response?.data?.status ?? this.readBody(notification, read).status));
        this.refresh();
      },
      error: () => { /* reported globally by ErrorInterceptor */ },
    });
  }

  private readBody(notification: any, read: boolean): {status: number; type: string} {
    return {
      status: read ? Constants.NOTIFICATION_STATUS_ACTIVE : Constants.NOTIFICATION_STATUS_INACTIVE,
      type: notification.note_id ? Constants.NOTE_NOTIFICATION : Constants.QUOTE_PRODUCT_NOTIFICATION
    };
  }

  /** The same follow-up the notification panel does after a read / unread. */
  private afterRead(notification: any, status: number): void {
    notification.status = status;
    const source = notification.note_reaction_id ? notification.noteReaction : notification;
    if (source?.note_id || notification.quote_product_id) {
      const record = source?.note_id ? notification.note : notification.quoteProduct;
      notification['quoteType'] = record?.quote?.type || null;
      this.subscribersService.updateNotificationStatus(notification);
    }
    this.subscribersService.updateUnreadNotification(PAGE_SIGNAL);
    this.noteState.publish({kind: 'read', notificationId: notification.id, status});
  }

  /**
   * Mark all as read (QA, 3 Nov): EVERY unread notification the reader has,
   * whatever the tab, filters or search — `POST notification/mark-all-as-read`,
   * as the old page and the drawer do. It asks first, in the redesign's Pebbl
   * confirm (console-v2 `.confirm` geometry), naming the inbox-wide unread
   * total — `unread_total` is unfiltered, the same set this marks. Then the rows
   * on screen and any notes card showing them turn read, the bell re-reads its
   * count, and the view and its counts reload, so the Unread view empties.
   */
  markAllRead(): void {
    if (!this.unreadTotal || this.markingAll) {
      return;
    }
    const unread = this.unreadTotal;
    const ref = this.ngbModal.open(PebblConfirmComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'pebbl-modal-window pebbl-confirm-window',
    });
    ref.componentInstance.title = 'Mark all as read?';
    ref.componentInstance.messageParts = [
      {text: unread === 1 ? 'Your ' : 'All '},
      {text: this.formatCount(unread) + (unread === 1 ? ' unread notification' : ' unread notifications'), bold: true},
      {text: ' will be marked as read, whatever filters are on.'},
    ];
    ref.componentInstance.confirmLabel = 'Mark all as read';
    ref.componentInstance.v2Layout = true;
    ref.result.then((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.markingAll = true;
      repaintView(this.changeDetection);
      this.notificationService.markAllNotificationsAsRead({to_user_id: this.userId}).pipe(
        takeUntil(this.destroy$),
        endLoading(this.changeDetection, () => this.markingAll = false)
      ).subscribe({
        next: () => {
          this.alertService.success(Constants.ALL_NOTIFICATION_MARKED_AS_READ_SUCCESS_MESSAGE);
          const read = Constants.NOTIFICATION_STATUS_ACTIVE;
          this.rows.forEach((row) => {
            if (row.raw.status != read) {
              row.raw.status = read;
              this.noteState.publish({kind: 'read', notificationId: row.raw.id, status: read});
            }
          });
          this.subscribersService.updateUnreadNotification(PAGE_SIGNAL);
          this.reload();
        },
        error: () => { /* reported globally by ErrorInterceptor */ },
      });
    }, () => { /* Cancel or Escape dismisses: nothing changes */ });
  }

  /** Only rows backed by a note can be liked. */
  canLike(row: PageRow): boolean {
    return !!likeTargetNote(row.raw)?.id;
  }

  isLiked(row: PageRow): boolean {
    return !!this.ownReaction(row.raw);
  }

  private ownReaction(notification: any): any {
    const note = likeTargetNote(notification);
    return (note?.note_reaction || []).find((reaction: any) => Number(reaction?.backend_user_id) === Number(this.userId)) || null;
  }

  /** Like / unlike the note behind the row (the note-reaction API). A like also
   *  acknowledges it: the row, and any other unread row about the same note, is
   *  marked read. Unliking leaves the read state alone. */
  toggleLike(row: PageRow): void {
    const notification = row.raw;
    const note = likeTargetNote(notification);
    if (!note?.id) {
      return;
    }
    const existingReaction = this.ownReaction(notification);

    if (existingReaction) {
      this.notificationService.deleteNoteReactions(existingReaction.id).pipe(takeUntil(this.destroy$)).subscribe(() => {
        note.note_reaction = (note.note_reaction || []).filter((reaction: any) => reaction?.id !== existingReaction.id);
        repaintView(this.changeDetection);
        this.noteState.publishReaction(note.id, this.userProfile, false);
      });
      return;
    }

    this.notificationService.updateNoteReactions({
      note_id: note.id,
      backend_user_id: this.userId
    }).pipe(takeUntil(this.destroy$)).subscribe((response: any) => {
      note.note_reaction = [...(note.note_reaction || []), response?.data];
      repaintView(this.changeDetection);
      this.noteState.publishReaction(note.id, this.userProfile, true, response?.data);
      this.rows
        .filter((other) => other !== row && other.raw.note_id && other.raw.note_id == note.id && this.isUnread(other))
        .forEach((other) => this.saveRead(other.raw, true, true));
      if (this.isUnread(row)) {
        this.saveRead(notification, true);
      }
    });
  }

  /************************************
   * Row actions end
   ************************************/
}

/* ── module helpers ─────────────────────────────────────────── */

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();
}

/** A picked calendar day as the API's `start_date` / `end_date` expect it. */
function ymd(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** "26 Oct 2026" — the Received chip's dates. */
function dayMonthYear(date: Date): string {
  return date.toLocaleDateString('en-AU', {day: 'numeric', month: 'short', year: 'numeric'});
}

/**
 * The instant a stored `created_at` stands for. The column holds Sydney wall
 * clock (`YYYY-MM-DD HH:mm:ss`, no zone — the PHP API writes it in Sydney time),
 * so it is read in Sydney, whatever the browser's own zone.
 */
function sydneyInstant(value: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value || '');
  if (!m) {
    const parsed = Date.parse(value);
    return isNaN(parsed) ? Date.now() : parsed;
  }
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  // Two passes settle the offset across a daylight-saving change.
  let instant = wall - sydneyOffset(wall);
  instant = wall - sydneyOffset(instant);
  return instant;
}

/** Sydney's offset from UTC, in ms, at an instant. */
function sydneyOffset(instant: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SYDNEY, hourCycle: 'h23',
    year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second')) - instant;
}

function dayKey(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-CA', {timeZone: SYDNEY}); // YYYY-MM-DD
}

/** "Today", "Yesterday", "Sat, 24 Oct", or with the year when it isn't this one. */
function dayLabel(timestamp: number): string {
  const today = dayKey(Date.now());
  const yesterday = dayKey(Date.now() - 86_400_000);
  const key = dayKey(timestamp);
  if (key === today) {
    return 'Today';
  }
  if (key === yesterday) {
    return 'Yesterday';
  }
  const sameYear = key.slice(0, 4) === today.slice(0, 4);
  return new Date(timestamp).toLocaleDateString('en-AU', {
    weekday: 'short', day: 'numeric', month: 'short', ...(sameYear ? {} : {year: 'numeric'}), timeZone: SYDNEY
  });
}
